/**
 * Step 5: 임베딩 모델 선택
 *
 * 모든 소스에 공통 적용되는 임베딩 모델을 선택합니다.
 * 파일이 있을 경우 예상 비용을 계산해 표시합니다.
 */

import { EMBEDDING_MODELS } from '../../../api';
import { Badge } from '../../ui';
import CheckIcon from '../../../assets/icons/common/check.svg?react';
import TextModeIcon from '../../../assets/icons/embedding/text.svg?react';
import ImageModeIcon from '../../../assets/icons/embedding/image.svg?react';

// Cohere 처리 방식 - 버튼 아이콘 · 제목 · 설명
const EMBEDDING_MODES: Array<{
  value: 'text' | 'multimodal';
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  desc: string;
}> = [
  { value: 'text',       icon: TextModeIcon,  title: '텍스트',   desc: '텍스트만 임베딩합니다. 이미지는 FM 파서로 텍스트 변환 후 처리됩니다.' },
  { value: 'multimodal', icon: ImageModeIcon, title: '멀티모달', desc: '이미지를 텍스트 변환 없이 그대로 임베딩합니다. 이미지의 시각 정보를 검색할 수 있습니다.' },
];

function formatCost(cost: number): string {
  if (cost === 0) return '$0.00';
  if (cost < 0.000001) return '< $0.000001';
  if (cost < 0.01) return `$${cost.toFixed(6)}`;
  return `$${cost.toFixed(2)}`;
}

interface EmbeddingStepProps {
  embeddingModelId: string;
  onEmbeddingModelIdChange: (id: string) => void;
  /** Cohere 임베딩 방식: 'text'(텍스트) | 'multimodal'(이미지 직접 임베딩) */
  embeddingMode?: 'text' | 'multimodal';
  onEmbeddingModeChange?: (mode: 'text' | 'multimodal') => void;
  /** 비용 추정용 — 파일 소스 선택 파일 목록 */
  files: File[];
  hasFile: boolean;
}

export function EmbeddingStep({
  embeddingModelId,
  onEmbeddingModelIdChange,
  embeddingMode = 'text',
  onEmbeddingModeChange,
  files,
  hasFile,
}: EmbeddingStepProps) {
  const selectedModel = EMBEDDING_MODELS.find((m) => m.id === embeddingModelId);
  const isCohere = embeddingModelId.startsWith('cohere.');
  const totalBytes = files.reduce((sum, f) => sum + f.size, 0);
  const estimatedTokens = Math.ceil((totalBytes / 1024) * 200);
  const estimatedCost = (estimatedTokens / 1000) * (selectedModel?.costPer1kTokens ?? 0.0001);

  return (
    <div className="pipe-section form-item--md">
      <div className="form-label">
        임베딩 모델
        <span className="optional">
          텍스트를 벡터로 변환할 모델을 선택하세요. 선택한 모델은 모든 소스에 공통 적용됩니다.
        </span>
      </div>

      {/* 모델 카드 — 전처리 파서 선택과 같은 공통 라디오 선택 카드(.choice-card) 세로 목록
          Cohere 선택 시 처리 방식이 같은 테두리 안에 펼쳐짐 (.pipe-parser-card 바깥 틀 공용) */}
      <div className="choice-cards choice-cards--single" role="radiogroup" aria-label="임베딩 모델">
        {EMBEDDING_MODELS.map((model) => {
          const isSelected = embeddingModelId === model.id;
          return (
            <div key={model.id} className={`pipe-parser-card${isSelected ? ' selected' : ''}`}>
              <button
                type="button"
                className={`choice-card${isSelected ? ' active' : ''}`}
                role="radio"
                aria-checked={isSelected}
                onClick={() => onEmbeddingModelIdChange(model.id)}
              >
                <span className="choice-card-radio" />
                <span className="choice-card-body">
                  <span className="choice-card-title">{model.name}</span>
                  <span className="choice-card-desc">{model.provider}</span>
                  <span className="choice-card-tags">
                    <Badge variant="type">{model.dimensions}차원</Badge>
                    <Badge variant="type">최대 {model.maxTokens.toLocaleString()}토큰</Badge>
                    <Badge variant="type">${model.costPer1kTokens} / 1K 토큰</Badge>
                  </span>
                </span>
              </button>

              {/* Cohere 선택 시: 텍스트 / 멀티모달 처리 방식 */}
              {isSelected && isCohere && (
                <div className="pipe-parser-fm-options">
                  <div className="pipe-settings-field">
                    <label className="pipe-settings-label">처리 방식</label>
                    {/* 버튼 2개 나란히 (청킹 전략 타일과 같은 선택 표시) - 아이콘 + 제목 + 설명 */}
                    <div className="pipe-mode-btns" role="radiogroup" aria-label="Cohere 처리 방식">
                      {EMBEDDING_MODES.map((m) => {
                        const active = embeddingMode === m.value;
                        const Icon = m.icon;
                        return (
                          <button
                            key={m.value}
                            type="button"
                            role="radio"
                            aria-checked={active}
                            className={`pipe-mode-btn${active ? ' active' : ''}`}
                            onClick={() => onEmbeddingModeChange?.(m.value)}
                          >
                            <span className="pipe-mode-btn-icon"><Icon /></span>
                            <span className="pipe-mode-btn-body">
                              <span className="pipe-mode-btn-title">{m.title}</span>
                              <span className="pipe-mode-btn-desc">{m.desc}</span>
                            </span>
                            {active && <span className="pipe-mode-btn-check" aria-hidden="true"><CheckIcon /></span>}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* 파일 소스가 있고 파일이 선택된 경우만 비용 추정 표시 */}
      {hasFile && files.length > 0 && (
        <div className="pipe-cost-estimate">
          <div className="pipe-cost-info">
            <span className="pipe-cost-label">예상 임베딩 비용</span>
            <span className="pipe-cost-meta">
              <span>파일 {files.length}개</span>
              <span>약 {estimatedTokens.toLocaleString()}토큰</span>
              <span>{selectedModel?.name}</span>
            </span>
          </div>
          <span className="pipe-cost-value">~{formatCost(estimatedCost)}</span>
        </div>
      )}
    </div>
  );
}
